/**
 * Store Module - ProductDetail Page
 *
 * Product detail page with image, description, and Add to Cart button.
 * Data is fetched from GET /v1/products/:id via the useProduct hook.
 * Funnel evaluation is enabled after add-to-cart using the runtime provider.
 */
import { useParams, Link, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useProduct } from '../hooks/use-products'
import { useCartStore } from '../store/cart-store'
import { FunnelExperience, FunnelRuntimeProvider } from '../../funnels/runtime'

function ProductDetailContent() {
  const { id = '' } = useParams<{ id: string }>()
  const location = useLocation()
  const { data: product, isLoading, isError, error } = useProduct(id)
  const addItem = useCartStore((state) => state.addItem)
  const [showUpsellExperience, setShowUpsellExperience] = useState(false)

  useEffect(() => {
    setShowUpsellExperience(false)
  }, [id])

  if (isLoading) {
    return (
      <div className="px-4 py-6 sm:px-0" aria-busy="true" aria-label="Loading product">
        <div className="animate-pulse">
          <div className="mb-6 h-4 w-40 rounded bg-tf-border" />
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="h-[400px] rounded-lg bg-tf-border lg:h-[500px]" />
            <div className="space-y-4">
              <div className="h-4 w-1/4 rounded bg-tf-border" />
              <div className="h-8 w-3/4 rounded bg-tf-border" />
              <div className="h-20 w-full rounded bg-tf-border" />
              <div className="h-12 w-40 rounded bg-tf-border" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isError || !product) {
    return (
      <div className="px-4 py-6 sm:px-0" role="alert">
        <h1 className="text-3xl font-bold text-tf-text mb-4">Product Not Found</h1>
        <p className="mb-4 text-tf-text-sub">
          {isError
            ? (error?.message ?? 'Failed to load the product. Please try again.')
            : "The product you're looking for doesn't exist."}
        </p>
        <Link to="/store/products" className="text-tf-primary hover:underline">
          Back to Products
        </Link>
      </div>
    )
  }

  // product is narrowed to Product here (the !product guard above ensured it)
  const resolvedProduct = product

  function handleAddToCart() {
    addItem({
      productId: resolvedProduct.id,
      name: resolvedProduct.name,
      price: 0,
      imageUrl: resolvedProduct.imageUrl,
    })
    setShowUpsellExperience(true)
  }

  return (
    <div className="px-4 py-6 sm:px-0">
      {/* Breadcrumb */}
      <nav className="mb-6 text-sm text-tf-text-muted" aria-label="Breadcrumb">
        <Link to="/store/products" className="hover:text-tf-text-link">
          Products
        </Link>
        <span className="mx-2" aria-hidden="true">/</span>
        <span className="text-tf-text">{resolvedProduct.name}</span>
      </nav>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Product Image */}
        <div className="overflow-hidden rounded-lg bg-tf-bg-card shadow">
          <img
            src={resolvedProduct.imageUrl}
            alt={resolvedProduct.name}
            className="h-[400px] w-full object-cover lg:h-[500px]"
          />
        </div>

        {/* Product Info */}
        <div>
          <p className="mb-2 text-sm font-medium uppercase tracking-wide text-tf-text-muted">
            {resolvedProduct.sku}
          </p>
          <h1 className="mb-4 text-3xl font-bold text-tf-text">
            {resolvedProduct.name}
          </h1>
          <p className="mb-8 text-base leading-relaxed text-tf-text-sub">
            {resolvedProduct.description}
          </p>

          {/* Add to Cart */}
          <button
            type="button"
            className="w-full rounded-lg bg-tf-primary px-6 py-4 text-lg font-bold text-white transition-colors hover:bg-tf-primary-hover focus:outline-none focus:ring-2 focus:ring-tf-primary focus:ring-offset-2 sm:w-auto"
            onClick={handleAddToCart}
          >
            Add to Cart
          </button>
          {showUpsellExperience && (
            <FunnelExperience
              pageType="pdp"
              context={{ url: location.pathname, productId: resolvedProduct.id }}
            />
          )}

          {/* Product Guarantees */}
          <div className="mt-8 border-t border-tf-border pt-6">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-tf-text-muted">
              Why buy from us
            </h3>
            <ul className="space-y-2 text-sm text-tf-text-sub">
              <li className="flex items-center gap-2">
                <span className="text-tf-success" aria-hidden="true">&#10003;</span>
                Free shipping on orders over $50
              </li>
              <li className="flex items-center gap-2">
                <span className="text-tf-success" aria-hidden="true">&#10003;</span>
                30-day money-back guarantee
              </li>
              <li className="flex items-center gap-2">
                <span className="text-tf-success" aria-hidden="true">&#10003;</span>
                Premium quality materials
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function ProductDetail() {
  return (
    <FunnelRuntimeProvider>
      <ProductDetailContent />
    </FunnelRuntimeProvider>
  )
}
